import type { ZodFormattedError } from "zod";
import type { inferRouterInputs } from "@trpc/server";
import type { AppRouter } from "@app-trpc/server/api/trpc/[trpc]";

export type GetGlobalPageSettingsInput =
  inferRouterInputs<AppRouter>["pages"]["getGlobalPageSettings"];
export type UpdateGlobalPageSettingsInput =
  inferRouterInputs<AppRouter>["pages"]["updateGlobalPageSettings"];
export class UpdateGlobalPageSettingsError extends Error {
  validationErrors?: ZodFormattedError<UpdateGlobalPageSettingsInput>;

  constructor(
    message: string,
    validationErrors?: ZodFormattedError<UpdateGlobalPageSettingsInput>
  ) {
    super(message);
    this.name = "UpdateGlobalPageSettingsError";
    this.validationErrors = validationErrors;
  }
}

export type GetPageSettingsInput =
  inferRouterInputs<AppRouter>["pages"]["getPageSettings"];
export type UpdatePageSettingsInput =
  inferRouterInputs<AppRouter>["pages"]["updatePageSettings"];
export class UpdatePageSettingsError extends Error {
  validationErrors?: ZodFormattedError<UpdatePageSettingsInput>;

  constructor(
    message: string,
    validationErrors?: ZodFormattedError<UpdatePageSettingsInput>
  ) {
    super(message);
    this.name = "UpdatePageSettingsError";
    this.validationErrors = validationErrors;
  }
}

export const usePageSettingsClient = () => {
  const client = useNuxtApp().$client;

  async function getGlobalPageSettings() {
    return client.pages.getGlobalPageSettings.query();
  }

  async function updateGlobalPageSettings(
    input: UpdateGlobalPageSettingsInput
  ) {
    return client.pages.updateGlobalPageSettings.mutate(input);
  }

  async function getAllPagesSettings() {
    return client.pages.getAllPagesSettings.query();
  }

  async function getPageSettings(input: GetPageSettingsInput) {
    return client.pages.getPageSettings.query(input);
  }

  async function updatePageSettings(input: UpdatePageSettingsInput) {
    try {
      await client.pages.updatePageSettings.mutate(input);
    } catch (e) {
      if (isTRPCClientError(e) && e.data?.zodError) {
        throw new UpdatePageSettingsError("Validation failed", e.data.zodError);
      } else {
        throw new UpdatePageSettingsError("Unknown error");
      }
    }
  }

  return {
    getAllPagesSettings,
    getPageSettings,
    updatePageSettings,
    getGlobalPageSettings,
    updateGlobalPageSettings,
  };
};
